import { useEffect, useRef, useState } from 'react';
import { REASON_COLORS, NOW_SLOTS } from './tiers';
import { NO_PROJECT_COLOR } from './projects';

// ============================================================
// TIER ROWS — NOW and TODAY, and the rows the Projects view hangs off a
// project's spine. Light rows inside the dark board, the NEW rail's rules:
// a 6 px bar, a 15 px title, one 11 px chip, the pin, the court pill, a
// chevron. NOW rows add the synopsis move line.
//
// The bar is the card's PROJECT color (Phase A): `colorOf(item)` from the
// parent, neutral slate when the card has no project. The NEW rail keeps
// its lane bar — an arrival has no project yet.
//
// Phase B hooks, all optional: `menu` (a node at the right, before the
// caret — the Projects view's ⋯), `editTitle` + `onRenameCommit` /
// `onRenameCancel` (the title becomes an input: Enter or blur saves, Esc
// cancels), `rowProps` (spread on the row — pointer handlers for drag) and
// `lifted` (the row is being dragged), `flash` (the Projects search landed
// here — a breathing ring until a click or 20 s). A row whose ProjectLocked
// or TitleLocked is true carries one small dot after the title.
//
// A row opens in place to the full card (the same component the columns
// render, handed in as `renderCard`), so a card seated in NOW or TODAY
// still reaches its Thread (N) links. Click anywhere on the head except
// the pin, pill, or chip; Enter on the focused row; Esc closes. The parent
// owns `expandedId`, so only one tier row is open at a time.
// ============================================================

// The one chip: a tier reason (tiers.js palette), or the Projects view's
// NOW / TODAY / DONE placement badges.
export const CHIP_COLORS = { ...REASON_COLORS, NOW: '#1f4e9e', TODAY: '#b8860b', DONE: '#5b6470', NEW: '#2e7d32' };
const barOf = (item, colorOf) => (typeof colorOf === 'function' && colorOf(item)) || NO_PROJECT_COLOR;

const Pin = ({ on }) => (
  <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
    <path d="M12.5 2.5l5 5-3 1-2.5 2.5.5 4.5-2 2-3.5-3.5L3 18l-1-1 4-4-3.5-3.5 2-2 4.5.5L11.5 5.5z"
      fill={on ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const NO_TOGGLE = '.TierRow-pin, .TierRow-pill, .TierRow-chip, .TierRow-menu, .TierRow-edit, .TierRow-lock';

// Inline editor: Enter or blur saves (trimmed, non-empty, changed), Esc
// cancels. Shared by the row title and the Projects view's band name.
export function InlineEditor({ initial, onCommit, onCancel, className = 'TierRow-edit', label = 'Title', placeholder = '' }) {
  const [value, setValue] = useState(initial);
  const ref = useRef(null);
  const done = useRef(false);
  useEffect(() => { ref.current?.focus(); ref.current?.select(); }, []);
  const commit = () => { if (done.current) return; done.current = true; const t = value.trim(); if (t && t !== initial) onCommit(t); else onCancel(); };
  const cancel = () => { if (done.current) return; done.current = true; onCancel(); };
  return (
    <input ref={ref} className={className} aria-label={label} placeholder={placeholder} value={value} onChange={e => setValue(e.target.value)}
      onClick={e => e.stopPropagation()} onPointerDown={e => e.stopPropagation()}
      onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { e.preventDefault(); cancel(); } }}
      onBlur={commit} />
  );
}

export function TierRow({ entry, showMove = false, onPin, expanded = false, onToggle, renderCard, colorOf, menu = null, editTitle = false, onRenameCommit, onRenameCancel, rowProps = null, lifted = false, flash = false }) {
  const { item, reason, pinned } = entry;
  const chip = entry.badge || reason || '';
  const lockTip = item.projectLocked && item.titleLocked ? 'placed and renamed by you' : item.projectLocked ? 'placed by you' : item.titleLocked ? 'renamed by you' : '';
  const court = item.court === 'Mine' ? 'mine' : item.court === 'Theirs' ? 'theirs' : item.court === 'Holding' ? 'holding' : '';
  const canOpen = typeof renderCard === 'function' && typeof onToggle === 'function';
  const toggle = () => { if (canOpen) onToggle(item.id); };
  const onHeadClick = (e) => {
    if (e.target.closest && e.target.closest(NO_TOGGLE)) return;
    toggle();
  };
  const onKeyDown = (e) => {
    if (e.key === 'Escape' && expanded) { e.preventDefault(); e.stopPropagation(); onToggle?.(item.id); return; }
    if (e.key === 'Enter' && e.target.classList && e.target.classList.contains('TierRow-head')) { e.preventDefault(); toggle(); }
  };
  return (
    <div className={'TierRow' + (pinned ? ' TierRow--pinned' : '') + (expanded ? ' TierRow--open' : '') + (lifted ? ' TierRow--lifted' : '') + (flash ? ' TierRow--flash' : '')}
      style={{ '--lane': barOf(item, colorOf) }} data-testid={`tier-${item.id}`} data-watch-id={item.id} onKeyDown={onKeyDown} {...(rowProps || {})}>
      <div className="TierRow-head" role={canOpen ? 'button' : undefined} tabIndex={canOpen ? 0 : undefined}
        aria-expanded={canOpen ? expanded : undefined} onClick={onHeadClick} data-testid={`tier-head-${item.id}`}>
        <span className="TierRow-lane" aria-hidden="true" />
        <div className="TierRow-body">
          {editTitle ? (
            <span className="TierRow-title TierRow-title--editing">
              {item.id != null && <span className="TierRow-id">#{item.id}</span>}
              <InlineEditor initial={item.title || ''} onCommit={(t) => onRenameCommit?.(item, t)} onCancel={() => onRenameCancel?.(item)} />
            </span>
          ) : (
            <span className="TierRow-title" title={item.title}>
              {item.id != null && <span className="TierRow-id">#{item.id}</span>}
              {item.title}
              {lockTip && <span className="TierRow-lock" title={lockTip} aria-label={lockTip} />}
            </span>
          )}
          {showMove && item.synopsis?.move && <div className="TierRow-move">{item.synopsis.move}</div>}
        </div>
        {chip && <span className="TierRow-chip" style={{ '--chip': CHIP_COLORS[chip] || '#5b6470' }}>{chip}</span>}
        {court && <span className={`TierRow-pill TierRow-pill--${court}`}>{item.court}</span>}
        <button type="button" className={'TierRow-pin' + (pinned ? ' TierRow-pin--on' : '')} aria-pressed={pinned}
          aria-label={pinned ? 'Unpin' : 'Pin to NOW'} title={pinned ? 'Unpin' : 'Pin to NOW'} onClick={() => onPin?.(item)}>
          <Pin on={pinned} />
        </button>
        {menu && <span className="TierRow-menu">{menu}</span>}
        {canOpen && <span className="TierRow-caret" aria-hidden="true">{expanded ? '▴' : '▾'}</span>}
      </div>
      {expanded && canOpen && (
        <div className="TierRow-card" data-testid={`tier-card-${item.id}`}>{renderCard(item)}</div>
      )}
    </div>
  );
}

export function NowTray({ now, onPin, expandedId = null, onToggle, renderCard, colorOf }) {
  const slots = Array.from({ length: NOW_SLOTS }, (_, n) => now[n] || null);
  return (
    <div className="TierRows" data-testid="now-tray">
      {slots.map((e, n) => e
        ? <TierRow key={e.item.id} entry={e} showMove onPin={onPin} expanded={expandedId === e.item.id} onToggle={onToggle} renderCard={renderCard} colorOf={colorOf} />
        : <div key={`cap-${n}`} className="TierRow TierRow--capacity" data-testid="now-capacity"><span className="TierRow-capacity">capacity</span></div>)}
    </div>
  );
}

export function TodayList({ today, onPin, expandedId = null, onToggle, renderCard, colorOf }) {
  if (!today.length) return <div className="Watch-empty">Nothing live today.</div>;
  return (
    <div className="TierRows" data-testid="today-list">
      {today.map(e => <TierRow key={e.item.id} entry={e} onPin={onPin} expanded={expandedId === e.item.id} onToggle={onToggle} renderCard={renderCard} colorOf={colorOf} />)}
    </div>
  );
}
